/**
 * O QUE OS FORMULÁRIOS DA OFICINA JÁ SABEM (pedido do dono, 31/10/2026:
 * "facilitar e agilizar ao máximo a vida de quem vai usar o sistema").
 *
 * Cada função responde UM campo a partir do que a tela já leu, e devolve
 * também DE ONDE veio a resposta, para a ajuda do campo dizer ("Do último
 * lançamento desta O.S."). Nenhuma decide nada pela pessoa: o valor entra
 * marcado como pré-preenchido e a marca some quando ela mexe.
 *
 * Puras, para o teste provar a regra sem desenhar a tela.
 */
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarHora, interpretarDataHora } from '@/lib/formato';

import type { Etapa, OrdemDaApi } from './api';

/* ------------------------------------------------------------ abrir O.S. */

/** A prioridade que a abertura usa quando ninguém escolheu outra (0019: 3 = Normal). */
export const PRIORIDADE_PADRAO = 3;
/** A prioridade "Máquina parada" da escala da 0019. */
export const PRIORIDADE_MAQUINA_PARADA = 2;

/**
 * Marcar "a máquina está parada" responde a prioridade, enquanto a pessoa não
 * a escolheu à mão: a escala da O.S. tem um degrau com esse nome. Segurança
 * (1) vem antes e nunca é rebaixada. Desmarcar devolve a padrão, se foi o
 * sistema que tinha subido.
 */
export function prioridadeDaParada(parada: boolean, prioridadeAtual: number, escolhidaAMao: boolean): number {
  if (escolhidaAMao) return prioridadeAtual;
  if (parada) return Math.min(prioridadeAtual, PRIORIDADE_MAQUINA_PARADA);
  return prioridadeAtual === PRIORIDADE_MAQUINA_PARADA ? PRIORIDADE_PADRAO : prioridadeAtual;
}

/**
 * A O.S. que a máquina escolhida já tem aberta: a ajuda do campo avisa antes
 * de a pessoa abrir uma segunda para a mesma falha. Não bloqueia: duas falhas
 * diferentes na mesma máquina são duas O.S.
 */
export function ordensAbertasDaMaquina(
  ordens: readonly Pick<OrdemDaApi, 'id' | 'numero' | 'equipamentoId' | 'etapa' | 'sintoma'>[],
  equipamentoId: string,
): readonly Pick<OrdemDaApi, 'id' | 'numero' | 'etapa' | 'sintoma'>[] {
  if (equipamentoId === '') return [];
  return ordens.filter(
    (o) => o.equipamentoId === equipamentoId && o.etapa !== 'liberada' && o.etapa !== 'encerrada' && o.etapa !== 'cancelada',
  );
}

/* ------------------------------------------------------------- etapas */

/**
 * A etapa que normalmente vem depois: o campo "Passar para" já abre nela, e
 * não na etapa em que a O.S. está (que deixava o botão desligado até alguém
 * trocar). "Aguardando peça" é desvio, não passo: ninguém vai para ela só
 * porque a anterior acabou, então a planejada segue para a execução, e a que
 * esperava peça também.
 */
export function proximaEtapa(etapa: Etapa): Etapa {
  switch (etapa) {
    case 'solicitada':
      return 'triagem';
    case 'triagem':
      return 'diagnostico';
    case 'diagnostico':
      return 'planejada';
    case 'planejada':
    case 'aguardando_peca':
      return 'em_execucao';
    case 'em_execucao':
      return 'teste';
    default:
      return etapa;
  }
}

/* ------------------------------------------------------- mão de obra */

export interface Apontamento {
  readonly mecanico: string;
  readonly inicio: string;
  readonly fim: string;
}

/**
 * As horas entre o início e o fim digitados, a cada tecla. `null` quando falta
 * um dos dois ou o fim não vem depois do início (aí não há conta a mostrar).
 */
export function horasDoApontamento(inicio: string, fim: string): number | null {
  const de = interpretarDataHora(inicio);
  const ate = interpretarDataHora(fim);
  if (de === null || ate === null || ate.getTime() <= de.getTime()) return null;
  return (ate.getTime() - de.getTime()) / 3_600_000;
}

/**
 * O início sugerido: o fim do último apontamento desta O.S., quando ele foi
 * HOJE e antes de agora. É o caso normal da oficina: o turno da manhã parou às
 * 11:00, o da tarde começa dali. Apontamento de outro dia não vale, porque
 * ninguém trabalhou a noite inteira na máquina.
 */
export function inicioSugerido(
  apontamentos: readonly Apontamento[],
  agora: Date = new Date(),
): { readonly valor: Date; readonly de: string } | null {
  const hoje = diaDaOperacao(agora);
  let ultimo: Apontamento | null = null;
  for (const a of apontamentos) {
    const fim = new Date(a.fim);
    if (Number.isNaN(fim.getTime()) || fim.getTime() >= agora.getTime() || diaDaOperacao(fim) !== hoje) continue;
    if (ultimo === null || fim.getTime() > new Date(ultimo.fim).getTime()) ultimo = a;
  }
  if (ultimo === null) return null;
  const valor = new Date(ultimo.fim);
  return { valor, de: `Continua do último apontamento, de ${ultimo.mecanico} até ${formatarHora(valor)}.` };
}

/** A pessoa logada, quando ela está na lista do campo. Casa pelo id, nunca pelo nome. */
export function pessoaDaSessao(pessoas: readonly { readonly id: string }[], usuarioId: string | null | undefined): string {
  if (!usuarioId) return '';
  return pessoas.some((p) => p.id === usuarioId) ? usuarioId : '';
}

/* -------------------------------------------------------------- peça */

/**
 * O depósito do último lançamento de peça DESTA O.S. que tirou de um depósito:
 * a O.S. costuma buscar tudo no mesmo almoxarifado. O detalhe da O.S. traz o
 * NOME do depósito, e a lista do campo, o id: casa pelo nome, e só quando o
 * nome é único na lista (dois "Almoxarifado" não respondem nada).
 */
export function depositoSugerido(
  pecas: readonly { readonly deposito: string | null }[],
  depositos: readonly { readonly id: string; readonly nome: string }[],
): string {
  for (let i = pecas.length - 1; i >= 0; i -= 1) {
    const nome = pecas[i]?.deposito;
    if (!nome) continue;
    const iguais = depositos.filter((d) => d.nome === nome);
    return iguais.length === 1 ? (iguais[0]?.id ?? '') : '';
  }
  return '';
}

/* ----------------------------------------------------------- parada */

/**
 * Com quem combinar a parada: a pessoa da operação com quem a última parada
 * DESTA MÁQUINA foi combinada (a frente costuma ter um encarregado só). Nunca
 * quem está gravando: a parada se combina com a operação, e o servidor recusa
 * a manutenção combinando consigo mesma.
 */
export function combinadaSugerida(
  ordens: readonly Pick<OrdemDaApi, 'id' | 'equipamentoId' | 'paradaProgramada'>[],
  equipamentoId: string,
  eu: string | null,
): { readonly id: string; readonly nome: string | null } | null {
  let melhor: { id: string; nome: string | null; em: number } | null = null;
  for (const o of ordens) {
    const p = o.paradaProgramada;
    if (o.equipamentoId !== equipamentoId || !p || p.combinadaComId === eu) continue;
    const em = new Date(p.combinadaEm).getTime();
    if (Number.isNaN(em)) continue;
    if (melhor === null || em > melhor.em) melhor = { id: p.combinadaComId, nome: p.combinadaCom, em };
  }
  return melhor === null ? null : { id: melhor.id, nome: melhor.nome };
}
